import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Typography } from '@pflegenavi/web-components';
import type { PayoutReconciliationEntry } from '@pflegenavi/service-provider-frontend/api';
import {
  useGetPayoutReconciliation,
  useGetServiceProviderPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import {
  csvDownload,
  useGetBatchReceiptDateString,
} from '@pflegenavi/shared/frontend';
import { dateAsYYYY_MM_DD } from '@pflegenavi/shared/utils';
import { useFormatting } from '@pflegenavi/frontend/localization';

interface PayoutReconciliationDialogProps {
  open: boolean;
  onClose: () => void;
  payoutId: string;
  initiationDate: Date;
}

export const PayoutReconciliationDialog: FC<
  PayoutReconciliationDialogProps
> = ({ open, onClose, payoutId, initiationDate }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const { getBatchReceiptDateString } = useGetBatchReceiptDateString();
  const { fCsvNumberCents } = useFormatting();

  const { refetch } = useGetPayoutReconciliation(payoutId, { enabled: false });
  const showReceiptNumber =
    useGetServiceProviderPhoenix().data?.data.receipt_image_analysis_enabled ??
    false;

  const formatDetails = useCallback(
    (entry: PayoutReconciliationEntry): string => {
      const minDate = entry.batch_min_receipt_date
        ? new Date(entry.batch_min_receipt_date)
        : undefined;
      const maxDate = entry.batch_max_receipt_date
        ? new Date(entry.batch_max_receipt_date)
        : undefined;
      const batchDate = entry.receipt_batch_entry?.receipt_batch?.receipt_date
        ? new Date(entry.receipt_batch_entry.receipt_batch.receipt_date)
        : undefined;

      return getBatchReceiptDateString(true, minDate, maxDate, batchDate);
    },
    [getBatchReceiptDateString]
  );

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const result = await refetch();
      if (result.error) {
        enqueueSnackbar(t('dialog.payout-reconciliation.download-error'), {
          variant: 'error',
        });
        return;
      }
      if (result.data) {
        const csvData = result.data.data.map((entry) => ({
          // Institution & Tenant first
          nursing_home_name: entry.nursing_home?.nursing_home_name ?? '',
          nursing_home_tenant: entry.nursing_home?.tenant ?? '',
          // Receipt date & count
          receipt_date: formatDetails(entry),
          batch_entry_count: entry.batch_entry_count ?? '',
          ...(showReceiptNumber
            ? {
                receipt_number: entry.receipt_batch_entry?.receipt_number ?? '',
              }
            : {}),
          service: entry.receipt_batch_entry?.service ?? '',
          // Date & Money
          performed_at: entry.performed_at,
          amount: fCsvNumberCents(entry.amount),
          currency: entry.currency,
          // Resident info
          resident_firstname:
            entry.receipt_batch_entry?.resident?.firstname ?? '',
          resident_lastname:
            entry.receipt_batch_entry?.resident?.lastname ?? '',
          // Batch info
          receipt_batch_id: entry.receipt_batch_entry?.receipt_batch?.id ?? '',
          batch_accepted_on:
            entry.receipt_batch_entry?.receipt_batch?.accepted_on ?? '',
          // Transaction details
          transaction_type: entry.mangopay_transaction_type,
        }));

        csvDownload({
          data: csvData,
          filename: `payout-reconciliation-${dateAsYYYY_MM_DD(
            initiationDate
          )}.csv`,
          headers: [
            // Institution & Tenant first
            t('csv.payout-reconciliation.headers.nursing-home-name'),
            t('csv.payout-reconciliation.headers.nursing-home-tenant'),
            // Receipt date & count
            t('csv.payout-reconciliation.headers.receipt-date'),
            t('csv.payout-reconciliation.headers.batch-entry-count'),
            ...(showReceiptNumber
              ? [t('csv.payout-reconciliation.headers.receipt-number')]
              : []),
            t('csv.payout-reconciliation.headers.service'),
            // Date & Money
            t('csv.payout-reconciliation.headers.performed-at'),
            t('csv.payout-reconciliation.headers.amount'),
            t('csv.payout-reconciliation.headers.currency'),
            // Resident info
            t('csv.payout-reconciliation.headers.resident-firstname'),
            t('csv.payout-reconciliation.headers.resident-lastname'),
            // Batch info
            t('csv.payout-reconciliation.headers.receipt-batch-id'),
            t('csv.payout-reconciliation.headers.batch-accepted-on'),
            // Transaction details
            t('csv.payout-reconciliation.headers.transaction-type'),
          ],
        });
        onClose();
      }
    } finally {
      setIsDownloading(false);
    }
  }, [
    refetch,
    initiationDate,
    t,
    onClose,
    enqueueSnackbar,
    formatDetails,
    fCsvNumberCents,
    showReceiptNumber,
  ]);

  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        <Typography variant="h5">
          {t('dialog.payout-reconciliation.title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {t('dialog.payout-reconciliation.content')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('dialog.payout-reconciliation.cancel')}
        </Button>
        <Button
          loading={isDownloading}
          onClick={handleDownload}
          variant="contained"
        >
          {t('dialog.payout-reconciliation.download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
